/** Author: Codex CLI Agent // Generation: P0 degrade/backpressure // Version: v3.2.240 // Modified: 2025-11-16T20:25:00-08:00 // One-line: 串行消费 + 背压降级 */

export type GameEvent = {
  type: string; // 'TURN_STARTED' | 'ACTION_MOVE' | 'ACTION_ATTACK' | 'TURN_ENDED' | 'AGENT_HEALTH_CHANGED' | 'AGENT_ELIMINATED' | 'RESOURCE_SPAWNED' | 'SAFEZONE_UPDATED' | ...
  payload?: any;
};

export interface PlaybackHandlers {
  onTurnStart?: (agentId: string, data?: any) => void; // 显示箭头/镜头跟随
  playMove?: (agentId: string, path: Array<{ x: number; y: number }>, data?: any) => Promise<void>; // 播放移动动画
  onTurnEnd?: (agentId?: string, data?: any) => void; // 回合结束的本地处理（可选）
  // 扩展处理器：战斗/状态/淘汰/提示
  playAttack?: (attackerId: string, defenderId: string, damage?: number, data?: any) => Promise<void>;
  playHpChange?: (agentId: string, delta: number, current?: number, max?: number, data?: any) => Promise<void>;
  playElimination?: (agentId: string, cause?: string, data?: any) => Promise<void>;
  showResourceMarker?: (data?: any) => Promise<void>;
  onResourceCollect?: (data?: any) => Promise<void>;
  showSafezoneMarker?: (type?: string, data?: any) => Promise<void>;
  onCombatStart?: (data?: any) => Promise<void>;
  onCombatMiss?: (data?: any) => Promise<void>;
  onCombatEnd?: (data?: any) => Promise<void>;
  onWeaponEquip?: (data?: any) => Promise<void>;
}

import ClientTrace from '../../services/telemetry/ClientTrace';

type TaskMeta = { type: string; agentId?: string; turn?: number };

export class PlaybackScheduler {
  private queue: Array<{ run: () => Promise<void>; meta: TaskMeta }> = [];
  private running = false;
  private paused = false;
  private idleResolvers: Array<() => void> = [];
  private cfg: { watchdogMs: number; resourceMarkerMs: number; safezoneMarkerMs: number };
  // P0-4 背压与降级
  private backlogThreshold = 80;
  private degradeMode = false;

  constructor(private handlers: PlaybackHandlers = {}, cfg?: Partial<{ watchdogMs: number; resourceMarkerMs: number; safezoneMarkerMs: number; backlogThreshold: number }>) {
    this.cfg = {
      watchdogMs: cfg?.watchdogMs ?? 6000,
      resourceMarkerMs: cfg?.resourceMarkerMs ?? 300,
      safezoneMarkerMs: cfg?.safezoneMarkerMs ?? 300,
    };
    if (typeof cfg?.backlogThreshold === 'number' && cfg.backlogThreshold > 0) {
      this.backlogThreshold = cfg.backlogThreshold;
    }
  }

  enqueue(evt: GameEvent): void {
    if (this.handleCombatSignal(evt)) {
      return;
    }
    const t = (evt?.payload && (evt.payload.turnNumber || evt.payload.turn)) || undefined;
    const aid = (evt?.payload && (evt.payload.agentId || evt.payload.id || evt.payload?.agent?.id)) || undefined;
    const meta: TaskMeta = { type: evt?.type || 'UNKNOWN', agentId: aid, turn: t };
    const run = this.translate(evt);
    try { ClientTrace.log('playback','playback',`ENQUEUE:${meta.type}`, { turn: meta.turn, agentId: meta.agentId, details: { qlen: this.queue.length } }); } catch {}
    this.queue.push({ run, meta });
    // 背压监测：进入/退出降级模式
    if (!this.degradeMode && this.queue.length > this.backlogThreshold) {
      this.degradeMode = true;
      try { ClientTrace.log('playback','playback','DEGRADE_ON', { details: { qlen: this.queue.length } }); } catch {}
    } else if (this.degradeMode && this.queue.length < Math.max(10, Math.floor(this.backlogThreshold / 2))) {
      this.degradeMode = false;
      try { ClientTrace.log('playback','playback','DEGRADE_OFF', { details: { qlen: this.queue.length } }); } catch {}
    }
    this.kick();
  }

  enqueueFixedDelay(delayMs: number, context?: { combatId?: string; plannedMs?: number }): void {
    const safeDelay = Math.max(0, Math.floor(Number.isFinite(delayMs) ? delayMs : 0));
    const waitType = context?.combatId ? 'WAIT_FIXED_COMBAT_MS' : 'WAIT_FIXED_MS';
    const meta: TaskMeta = { type: waitType, agentId: context?.combatId };
    const run = async () => {
      if (safeDelay === 0) return;
      await new Promise<void>((resolve) => setTimeout(resolve, safeDelay));
    };
    try {
      ClientTrace.log('playback','playback',`ENQUEUE:${meta.type}`, {
        agentId: meta.agentId,
        details: { qlen: this.queue.length, plannedMs: safeDelay }
      });
    } catch {}
    this.queue.push({ run, meta });
    this.kick();
  }

  start(): void { this.paused = false; this.kick(); }
  pause(): void { this.paused = true; }
  resume(): void { this.paused = false; this.kick(); }

  whenIdle(): Promise<void> {
    if (!this.running && this.queue.length === 0) return Promise.resolve();
    return new Promise<void>(resolve => this.idleResolvers.push(resolve));
  }

  private kick(): void {
    if (this.running || this.paused) return;
    const next = this.queue.shift();
    if (!next) { this.resolveIdle(); return; }
    this.running = true;
    try { ClientTrace.log('playback','playback',`START_TASK:${next.meta.type}`, { agentId: next.meta.agentId, turn: next.meta.turn, details: { qlen: this.queue.length } }); } catch {}
    this.runWithWatchdog(next.run, next.meta).finally(() => {
      try { ClientTrace.log('playback','playback',`END_TASK:${next.meta.type}`, { agentId: next.meta.agentId, turn: next.meta.turn, details: { qlen: this.queue.length } }); } catch {}
      this.running = false;
      // 微任务后继续，防止递归深栈
      Promise.resolve().then(() => this.kick());
    });
  }

  private resolveIdle(): void {
    if (this.running || this.queue.length > 0) return;
    const resolvers = this.idleResolvers.splice(0);
    resolvers.forEach(r => { try { r(); } catch {} });
  }

  private translate(evt: GameEvent): () => Promise<void> {
    const { type, payload } = evt || ({} as any);
    switch (type) {
      case 'TURN_STARTED': {
        const agentId = payload?.agentId ?? payload?.id ?? payload?.agent?.id;
        return async () => { this.handlers.onTurnStart?.(agentId, payload); };
      }
      case 'ACTION_MOVE': {
        const agentId = payload?.agentId ?? payload?.id ?? payload?.agent?.id;
        const path = payload?.path || payload?.positions || [];
        return async () => {
          if (this.handlers.playMove) {
            await this.handlers.playMove(agentId, path, payload);
          }
        };
      }
      case 'AGENT_HEALTH_CHANGED': {
        const agentId = payload?.agentId ?? payload?.id;
        const delta = payload?.changeAmount ?? payload?.delta ?? 0;
        return async () => {
          if (this.handlers.playHpChange) {
            await this.handlers.playHpChange(agentId, delta, payload?.current, payload?.max, payload);
          }
        };
      }
      case 'AGENT_ELIMINATED': {
        const agentId = payload?.agentId ?? payload?.id;
        const cause = payload?.cause ?? payload?.elimination?.cause;
        return async () => {
          if (this.handlers.playElimination) {
            await this.handlers.playElimination(agentId, cause, payload);
          }
        };
      }
      case 'RESOURCE_SPAWNED': {
        return async () => {
          if (this.handlers.showResourceMarker) { await this.handlers.showResourceMarker(payload); }
          else { await new Promise<void>(r => setTimeout(r, this.degradeMode ? 30 : this.cfg.resourceMarkerMs)); }
        };
      }
      case 'RESOURCE_COLLECTED': {
        return async () => {
          if (this.handlers.onResourceCollect) { await this.handlers.onResourceCollect(payload); }
          else if (this.handlers.showResourceMarker) { await this.handlers.showResourceMarker(payload); }
          else { await new Promise<void>(r => setTimeout(r, this.degradeMode ? 30 : this.cfg.resourceMarkerMs)); }
        };
      }
      case 'WEAPON_EQUIPPED': {
        return async () => {
          if (this.handlers.onWeaponEquip) {
            await this.handlers.onWeaponEquip(payload);
          } else {
            // 装备无显式marker，回退为极短等待
            await new Promise<void>(r => setTimeout(r, 120));
          }
        };
      }
      case 'SAFEZONE_WARNING':
      case 'SAFEZONE_CONTRACTION_STARTED':
      case 'SAFEZONE_CONTRACTION_COMPLETED':
      case 'SAFEZONE_UPDATED': {
        const t = type;
        return async () => {
          if (this.handlers.showSafezoneMarker) { await this.handlers.showSafezoneMarker(t, payload); }
          else { await new Promise<void>(r => setTimeout(r, this.degradeMode ? 30 : this.cfg.safezoneMarkerMs)); }
        };
      }
      case 'TURN_ENDED': {
        const agentId = payload?.agentId ?? payload?.id ?? payload?.agent?.id;
        return async () => { this.handlers.onTurnEnd?.(agentId, payload); };
      }
      default: {
        // 未知事件：当作同步完成
        return async () => {};
      }
    }
  }

  private handleCombatSignal(evt: GameEvent): boolean {
    const payload = evt?.payload || {};
    const invoke = (result: any) => {
      if (result && typeof result.then === 'function') {
        result.catch(() => {});
      }
    };
    switch (evt.type) {
      case 'COMBAT_SIGNAL_STARTED': {
        if (this.handlers.onCombatStart) {
          invoke(this.handlers.onCombatStart(payload));
        }
        return true;
      }
      case 'COMBAT_SIGNAL_ATTACK': {
        if (this.handlers.playAttack) {
          const attackerId = payload.attackerId ?? payload.attacker;
          const defenderId = payload.defenderId ?? payload.defender;
          invoke(this.handlers.playAttack(attackerId, defenderId, payload.damage, payload));
        }
        return true;
      }
      case 'COMBAT_SIGNAL_MISS': {
        if (this.handlers.onCombatMiss) {
          invoke(this.handlers.onCombatMiss(payload));
        }
        return true;
      }
      case 'COMBAT_SIGNAL_HP': {
        if (this.handlers.playHpChange) {
          const agentId = payload.agentId ?? payload.id;
          const delta = payload.changeAmount ?? payload.delta ?? 0;
          invoke(this.handlers.playHpChange(agentId, delta, payload.current, payload.max, payload));
        }
        return true;
      }
      case 'COMBAT_SIGNAL_ENDED': {
        if (this.handlers.onCombatEnd) {
          invoke(this.handlers.onCombatEnd(payload));
        }
        return true;
      }
      default:
        return false;
    }
  }

  private async runWithWatchdog(task: () => Promise<void>, meta?: TaskMeta): Promise<void> {
    let done = false;
    const p = task().then(() => { done = true; });
    const w = new Promise<void>(resolve => setTimeout(resolve, this.cfg.watchdogMs));
    await Promise.race([p, w]);
    if (!done) {
      try { ClientTrace.log('playback','playback',`WATCHDOG_TIMEOUT:${meta?.type||'UNKNOWN'}`, { agentId: meta?.agentId, turn: meta?.turn, details: { qlen: this.queue.length } }); } catch {}
    }
  }
}

export default PlaybackScheduler;
